import { getOrgIdFromToken, getUserIdFromToken } from "@/api/utils";
import { clearCanvasSession } from "@/pages/canvas/hooks/canvasSessionStorage";
import { clearViewport } from "@/pages/canvas/hooks/useCanvasViewportStorage";
import { trackTabOpened } from "@/utils/analytics";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocalStorageState } from "ahooks";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type CanvasTab = {
  kind: "canvas";
  /** Stable unique id for this tab (same as canvasId for canvas tabs). */
  id: string;
  name: string;
  /**
   * A create is in flight and the server has not minted the canvas id yet, so
   * `id` is a strip-local placeholder. Lives in memory only — never persisted,
   * never preloaded, and replaced in place by the real tab on success.
   */
  pending?: true;
  // No imageUrl here: tab hero previews resolve from the ["canvas", id]
  // query (kept observed per open tab by TabPreloadContext). Persisting a
  // presigned URL in localStorage was the one spot left doing so after the
  // query-cache redaction scrubbed them everywhere else.
};

export type ChatTab = {
  kind: "chat";
  /** Stable unique id for this tab (same as flowSessionId for chat tabs). */
  id: string;
  name: string;
};

export type OpenTab = CanvasTab | ChatTab;

interface OpenTabsContextValue {
  openTabs: OpenTab[];
  /** Id of the pending canvas tab, when a create is in flight. */
  pendingTabId: string | null;
  addTab: (tab: OpenTab) => void;
  /** Appends a spinner tab for a create in flight; returns its placeholder id. */
  addPendingCanvasTab: (name: string) => string;
  /** Swaps the pending tab for the real one, in the same slot. */
  resolvePendingCanvasTab: (pendingId: string, tab: CanvasTab) => void;
  removePendingCanvasTab: (pendingId: string) => void;
  closeTab: (id: string) => void;
  closeOtherTabs: (id: string) => void;
  closeTabsToRight: (id: string) => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  clearTabs: () => void;
}

const STORAGE_KEY = "open-workspace-tabs";
const IDENTITY_KEY = "open-workspace-tabs:identity";
const MAX_TABS = 20;

function isValidTab(t: unknown): t is OpenTab {
  if (!t || typeof t !== "object") return false;
  const tab = t as Record<string, unknown>;
  if (typeof tab.id !== "string" || typeof tab.name !== "string") return false;
  if (tab.kind !== "canvas" && tab.kind !== "chat") return false;
  return true;
}

function migrateTab(raw: unknown): OpenTab | null {
  if (isValidTab(raw)) {
    // Rebuild with known fields only: stored entries from before the
    // imageUrl retirement carry a presigned URL that must not survive the
    // next serialize round-trip.
    return raw.kind === "canvas"
      ? { kind: "canvas", id: raw.id, name: raw.name }
      : { kind: "chat", id: raw.id, name: raw.name };
  }
  // Migrate legacy `{ canvasId, name }` shape (older still carried a
  // presigned imageUrl — deliberately dropped).
  if (raw && typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    if (typeof r.canvasId === "string") {
      return {
        kind: "canvas",
        id: r.canvasId,
        name: typeof r.name === "string" ? r.name : "Untitled",
      };
    }
  }
  return null;
}

function deserializeOpenTabs(value: string): OpenTab[] {
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(migrateTab)
      .filter((t): t is OpenTab => t !== null)
      .slice(-MAX_TABS);
  } catch {
    return [];
  }
}

const OpenTabsContext = createContext<OpenTabsContextValue | null>(null);

const EMPTY_TABS: OpenTab[] = [];

let pendingTabSequence = 0;

export function OpenTabsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

  const [openTabs, setOpenTabs] = useLocalStorageState<OpenTab[]>(STORAGE_KEY, {
    defaultValue: [],
    deserializer: deserializeOpenTabs,
    listenStorageChange: true,
  });

  // One-time scrub: entries written before the imageUrl retirement keep a
  // presigned URL in the stored JSON until the next write. Force a rewrite
  // through the field-dropping serialize path instead of waiting for one.
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)?.includes('"imageUrl"')) {
      setOpenTabs((current) => current ?? []);
    }
  }, [setOpenTabs]);

  // Tabs are only exposed once we've confirmed that the localStorage contents
  // belong to the currently-authenticated identity. Until then, we render an
  // empty list to avoid briefly leaking a previous user's/org's tabs while
  // Auth0 resolves and we verify the token.
  const [identityReady, setIdentityReady] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      localStorage.removeItem(IDENTITY_KEY);
      setOpenTabs([]);
      setIdentityReady(true);
      return;
    }

    let cancelled = false;
    setIdentityReady(false);
    getAccessTokenSilently()
      .then((token) => {
        if (cancelled) return;
        const identity = `${getOrgIdFromToken(token)}:${getUserIdFromToken(token)}`;
        const stored = localStorage.getItem(IDENTITY_KEY);
        if (stored !== identity) {
          setOpenTabs([]);
          localStorage.setItem(IDENTITY_KEY, identity);
        }
        setIdentityReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setOpenTabs([]);
        localStorage.removeItem(IDENTITY_KEY);
        setIdentityReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, setOpenTabs]);

  const ready = isAuthenticated && identityReady;
  const persistedTabs = ready ? (openTabs ?? []) : EMPTY_TABS;

  // Kept out of `openTabs` entirely rather than filtered on write: the tab is
  // a placeholder for an id the server has not minted, so nothing that
  // persists, preloads or navigates by id may see it.
  const [pendingTab, setPendingTabState] = useState<CanvasTab | null>(null);
  const pendingTabRef = useRef<CanvasTab | null>(null);
  const setPendingTab = useCallback((next: CanvasTab | null) => {
    pendingTabRef.current = next;
    setPendingTabState(next);
  }, []);

  const tabs = useMemo(
    () => (pendingTab ? [...persistedTabs, pendingTab] : persistedTabs),
    [persistedTabs, pendingTab],
  );

  // ahooks' useLocalStorageState functional updater computes from the state
  // captured at the LAST RENDER, not from React's queued previous state. A
  // burst of mutations within one render cycle (e.g. several canvas.deleted /
  // flow_session.deleted websocket events from a workspace cascade landing
  // back-to-back) would each derive from the same stale snapshot and the last
  // write would win — closing only one of N tabs. The ref is updated
  // synchronously on every write so each mutation in a burst sees the
  // previous one's result; the effect re-syncs it after external changes
  // (cross-tab storage events).
  const tabsRef = useRef<OpenTab[]>(openTabs ?? []);
  useEffect(() => {
    tabsRef.current = openTabs ?? [];
  }, [openTabs]);

  const applyTabsUpdate = useCallback(
    (updater: (prev: OpenTab[]) => OpenTab[]) => {
      const prev = tabsRef.current;
      const next = updater(prev);
      tabsRef.current = next;
      // Any canvas tab removed by this update (close, close-others/right/all,
      // trash, or MAX_TABS overflow) drops its saved viewport + session snapshot
      // so reopening it auto-fits to content and starts with a clean layout
      // instead of restoring the stale zoom / chat / right pane.
      const nextIds = new Set(next.map((t) => t.id));
      for (const t of prev) {
        if (t.kind === "canvas" && !nextIds.has(t.id)) {
          clearViewport(t.id);
          clearCanvasSession(t.id);
        }
      }
      setOpenTabs(next);
    },
    [setOpenTabs],
  );

  const addTab = useCallback(
    (tab: OpenTab) => {
      if (!ready) return;
      // New-ness is decided outside the updater (StrictMode runs it twice);
      // the tab-sync effects re-add on every route entry and rename, so only
      // a genuine insert counts as an open.
      const isNew = !tabsRef.current.some(
        (t) => t.id === tab.id && t.kind === tab.kind,
      );
      applyTabsUpdate((p) => {
        const idx = p.findIndex((t) => t.id === tab.id && t.kind === tab.kind);
        if (idx !== -1) {
          return p.map((t, i) => (i === idx ? { ...t, ...tab } : t));
        }
        return [...p, tab].slice(-MAX_TABS);
      });
      if (isNew) {
        trackTabOpened({
          tabKind: tab.kind,
          openTabCount: tabsRef.current.length,
        });
      }
    },
    [ready, applyTabsUpdate],
  );

  const addPendingCanvasTab = useCallback(
    (name: string) => {
      const id = `pending-canvas:${++pendingTabSequence}`;
      setPendingTab({ kind: "canvas", id, name, pending: true });
      return id;
    },
    [setPendingTab],
  );

  const removePendingCanvasTab = useCallback(
    (pendingId: string) => {
      if (pendingTabRef.current?.id !== pendingId) return;
      setPendingTab(null);
    },
    [setPendingTab],
  );

  const resolvePendingCanvasTab = useCallback(
    (pendingId: string, tab: CanvasTab) => {
      // Clearing the placeholder and appending the real tab in one batch puts
      // it in the slot the placeholder held: both live at the end of the list.
      if (pendingTabRef.current?.id === pendingId) setPendingTab(null);
      addTab(tab);
    },
    [setPendingTab, addTab],
  );

  const closeTab = useCallback(
    (id: string) => {
      if (!ready) return;
      applyTabsUpdate((p) => p.filter((t) => t.id !== id));
    },
    [ready, applyTabsUpdate],
  );

  const closeOtherTabs = useCallback(
    (id: string) => {
      if (!ready) return;
      applyTabsUpdate((p) => p.filter((t) => t.id === id));
    },
    [ready, applyTabsUpdate],
  );

  const closeTabsToRight = useCallback(
    (id: string) => {
      if (!ready) return;
      applyTabsUpdate((p) => {
        const idx = p.findIndex((t) => t.id === id);
        if (idx === -1) return p;
        return p.slice(0, idx + 1);
      });
    },
    [ready, applyTabsUpdate],
  );

  const reorderTabs = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!ready) return;
      applyTabsUpdate((prev) => {
        const p = [...prev];
        p.splice(toIndex, 0, p.splice(fromIndex, 1)[0]);
        return p;
      });
    },
    [ready, applyTabsUpdate],
  );

  const clearTabs = useCallback(() => {
    applyTabsUpdate(() => []);
  }, [applyTabsUpdate]);

  const value = useMemo(
    () => ({
      openTabs: tabs,
      pendingTabId: pendingTab?.id ?? null,
      addTab,
      addPendingCanvasTab,
      resolvePendingCanvasTab,
      removePendingCanvasTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      reorderTabs,
      clearTabs,
    }),
    [
      tabs,
      pendingTab?.id,
      addTab,
      addPendingCanvasTab,
      resolvePendingCanvasTab,
      removePendingCanvasTab,
      closeTab,
      closeOtherTabs,
      closeTabsToRight,
      reorderTabs,
      clearTabs,
    ],
  );

  return (
    <OpenTabsContext.Provider value={value}>
      {children}
    </OpenTabsContext.Provider>
  );
}

export function useOpenTabs() {
  const ctx = useContext(OpenTabsContext);
  if (!ctx)
    throw new Error("useOpenTabs must be used within an OpenTabsProvider");
  return ctx;
}
